import { useState } from 'react';
import { Search, FileText, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useDceSearch } from '../../hooks';
import {
  DCE_DOCUMENT_TYPE_LABELS,
  type DceSearchResultChunk,
} from '../../types/dce-upload.types';

interface DceSearchPanelProps {
  opportunityId: number;
}

const EXAMPLE_QUERIES = [
  "Quels sont les délais d'exécution ?",
  'Critères d\'attribution',
  'Pénalités de retard',
  'Modalités de paiement',
];

export function DceSearchPanel({ opportunityId }: DceSearchPanelProps) {
  const [query, setQuery] = useState('');
  const search = useDceSearch(opportunityId);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    search.mutate({ query: query.trim(), topK: 10 });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Search className="h-4 w-4" />
          Recherche dans le DCE
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={onSubmit} className="flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ex : quelles sont les pénalités de retard ?"
            disabled={search.isPending}
            className="flex-1"
          />
          <Button type="submit" disabled={search.isPending || !query.trim()}>
            {search.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </form>

        {!search.data && !search.isPending && (
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_QUERIES.map((q) => (
              <Button
                key={q}
                type="button"
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={() => {
                  setQuery(q);
                  search.mutate({ query: q, topK: 10 });
                }}
              >
                {q}
              </Button>
            ))}
          </div>
        )}

        {search.isError && (
          <p className="text-sm text-red-600">
            Erreur : {(search.error as Error)?.message ?? 'recherche échouée'}
          </p>
        )}

        {search.data && search.data.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            Aucun extrait pertinent trouvé. Vérifie qu'au moins un import DCE est
            en statut « Terminé » (l'indexation se déclenche après l'extraction texte).
          </p>
        )}

        {search.data && search.data.length > 0 && (
          <ul className="space-y-3">
            {search.data.map((result) => (
              <SearchResultRow key={result.chunkId} result={result} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SearchResultRow({ result }: { result: DceSearchResultChunk }) {
  const pct = Math.round(result.similarity * 100);
  return (
    <li className="border rounded-md p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-medium">{result.documentFilename}</span>
        {result.documentType && (
          <Badge variant="outline" className="text-[10px]">
            {DCE_DOCUMENT_TYPE_LABELS[result.documentType]}
          </Badge>
        )}
        {result.sectionTitle && (
          <span className="text-muted-foreground">· § {result.sectionTitle}</span>
        )}
        {result.pageNumber !== null && (
          <span className="text-muted-foreground">· p.{result.pageNumber}</span>
        )}
        <span className="ml-auto tabular-nums text-emerald-600 font-medium">
          {pct}%
        </span>
      </div>
      <p className="text-sm whitespace-pre-wrap line-clamp-6 text-muted-foreground">
        {result.content}
      </p>
    </li>
  );
}
